/**
 * Spec resolver — the heart of the explorer.
 *
 *   resolveQueryArgs(spec)            → params for the query_form_view RPC
 *   resolveTable(spec, catalog, data) → flat { columns, rows } for the grid
 *
 * Handles both repeatability axes (architecture §2):
 *   • repeatable FORM  → long / wide / aggregate / pinned   (across instances)
 *   • repeatable_array → join / wide / aggregate / separate (within one cell)
 * and computes calculated fields client-side.
 *
 * Pure: no React, no Supabase, no dayjs. Unit-tested in
 * apps/web/tests/unit/data-explorer.test.ts.
 */
import { computeCalculatedFields } from '@/lib/form-engine'
import {
  WIDE_DEFAULT_CAP,
  META_COLUMNS,
  DEFAULT_RECORD_FORM_STRATEGY,
  type DataViewSpec,
  type ViewColumn,
  type FilterOp,
  type AggregateFn,
  type ArrayStrategy,
  type FormInstanceStrategy,
} from '../types/data-view-spec'
import type { FormCatalog, CatalogField } from './column-catalog'
import type { FieldType } from '@/features/form-builder/api/form-builder-types'

// ─── RPC shapes ────────────────────────────────────────────────────────────────

export interface InstanceObj {
  submission_id: string
  instance_index: number
  submission_status: string
  form_version_id: string
  soft_warning_count: number
  values: Record<string, unknown> | null
}

export interface LongRpcRow {
  record_id: string
  display_id: string
  record_status: string
  record_number: number
  record_created_at: string
  record_updated_at: string
  owner_id: string | null
  submission_id: string
  instance_index: number
  submission_status: string
  form_version_id: string
  soft_warning_count: number
  values: Record<string, unknown> | null
}

export interface GroupedRpcRow {
  record_id: string
  display_id: string
  record_status: string
  record_number: number
  record_created_at: string
  record_updated_at: string
  owner_id: string | null
  instances: InstanceObj[]
}

export interface QueryFormViewResult {
  layout: 'long' | 'grouped'
  total: number
  rows: (LongRpcRow | GroupedRpcRow)[]
}

export interface QueryFormViewArgs {
  formId: string
  fieldKeys: string[]
  filters: { key: string; op: FilterOp; value?: string }[]
  sortKey: string | null
  sortDir: 'asc' | 'desc'
  layout: 'long' | 'grouped'
  includeDeleted: boolean
  search: string | null
}

// ─── Resolved (display) shapes ─────────────────────────────────────────────────

export interface ResolvedColumn {
  id: string
  label: string
  kind: 'meta' | 'field' | 'calc' | 'derived'
  metaKey?: string
  catalogField?: CatalogField
  fieldType?: FieldType
  /** wide layout: which instance index this column represents. */
  instanceIndex?: number
}

export interface ResolvedRow {
  id: string
  recordId: string
  displayId: string
  recordStatus: string
  submissionId?: string
  instanceIndex?: number
  softWarningCount: number
  formVersionId?: string
  /** colId → raw value (scalar) for type-aware formatting in the cell. */
  cells: Record<string, unknown>
  /** colId → pre-formatted string (derived/array/aggregate columns). */
  display: Record<string, string>
  /** colId → raw array entries, for repeatable_array "separate" drilldown. */
  arrays: Record<string, unknown[]>
}

export interface ResolvedTable {
  columns: ResolvedColumn[]
  rows: ResolvedRow[]
  total: number
}

const META_KEYS = new Set(META_COLUMNS.map((m) => m.key))

// ─── 1. spec → RPC args ─────────────────────────────────────────────────────────

export function resolveQueryArgs(spec: DataViewSpec): QueryFormViewArgs {
  const formId = spec.base.kind === 'form' ? spec.base.formId : ''
  // Every field column (incl. those feeding calc inputs the user picked) must be
  // fetched. Calc fields themselves are computed client-side from their inputs;
  // we still request any field_key column the user added.
  const fieldKeys = Array.from(
    new Set(
      spec.columns
        .filter((c) => c.source === 'field')
        .map((c) => c.key),
    ),
  )
  const layout: 'long' | 'grouped' =
    spec.instanceStrategy.mode === 'long' ? 'long' : 'grouped'

  return {
    formId,
    fieldKeys,
    filters: spec.filters.map((f) => ({ key: f.key, op: f.op, value: f.value })),
    sortKey: spec.sort?.key ?? null,
    sortDir: spec.sort?.dir ?? 'asc',
    layout,
    includeDeleted: spec.includeDeleted ?? false,
    search: spec.search?.trim() || null,
  }
}

// ─── 2. column resolution ────────────────────────────────────────────────────────

function metaLabel(key: string): string {
  return META_COLUMNS.find((m) => m.key === key)?.label ?? key
}
function metaType(key: string): FieldType {
  return (META_COLUMNS.find((m) => m.key === key)?.type ?? 'text') as FieldType
}

/** Columns produced by a single scalar field, given the instance mode. */
function fieldColumnsFor(
  col: ViewColumn,
  cat: CatalogField | undefined,
  isCalc: boolean,
  mode: DataViewSpec['instanceStrategy']['mode'],
  cap: number,
): ResolvedColumn[] {
  const label = col.label ?? cat?.label ?? col.key
  const kind: ResolvedColumn['kind'] = isCalc
    ? 'calc'
    : cat?.isRepeatableArray
      ? 'derived'
      : 'field'
  const fieldType = cat?.fieldType

  // repeatable_array always collapses to a single derived cell (its own array
  // strategy governs the content); it is never instance-widened.
  if (cat?.isRepeatableArray) {
    return [{ id: col.id, label, kind: 'derived', catalogField: cat, fieldType }]
  }

  if (mode === 'wide') {
    return Array.from({ length: cap }, (_, i) => ({
      id: `${col.id}@${i + 1}`,
      label: `${label} (${i + 1})`,
      kind,
      catalogField: cat,
      fieldType,
      instanceIndex: i + 1,
    }))
  }
  // long / aggregate / pinned → one column
  return [{ id: col.id, label, kind, catalogField: cat, fieldType }]
}

export function resolveColumns(spec: DataViewSpec, catalog: FormCatalog): ResolvedColumn[] {
  const mode = spec.instanceStrategy.mode
  const cap = spec.instanceStrategy.cap ?? WIDE_DEFAULT_CAP
  const out: ResolvedColumn[] = []

  for (const col of spec.columns) {
    if (col.hidden) continue
    if (col.source === 'record_meta') {
      // instance_index only meaningful in long layout
      if (col.key === 'instance_index' && mode !== 'long') continue
      out.push({
        id: col.id,
        label: col.label ?? metaLabel(col.key),
        kind: 'meta',
        metaKey: col.key,
        fieldType: metaType(col.key),
      })
      continue
    }
    const isCalc = !!catalog.calculatedByKey[col.key]
    const cat = catalog.byKey[col.key]
    out.push(...fieldColumnsFor(col, cat, isCalc, mode, cap))
  }
  return out
}

// ─── 3. value helpers ─────────────────────────────────────────────────────────

function metaValue(row: { display_id: string; record_status: string; record_created_at: string; record_updated_at: string }, key: string): unknown {
  switch (key) {
    case 'display_id': return row.display_id
    case 'record_status': return row.record_status
    case 'record_created_at': return row.record_created_at
    case 'record_updated_at': return row.record_updated_at
    default: return undefined
  }
}

/** Merge a submission's stored values with its computed calculated fields. */
function withCalc(catalog: FormCatalog, values: Record<string, unknown> | null): Record<string, unknown> {
  const base = values ?? {}
  if (catalog.calculated.length === 0) return base
  const calc = computeCalculatedFields(catalog.allFieldDefs, base)
  return { ...base, ...calc }
}

function toNumber(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

/** Aggregate a field's values across instances. Returns {value, display}. */
function aggregateField(
  fn: AggregateFn,
  vals: unknown[],
): { value: unknown; display?: string } {
  const nonEmpty = vals.filter((v) => v !== null && v !== undefined && v !== '')
  switch (fn) {
    case 'count':
      return { value: nonEmpty.length, display: String(nonEmpty.length) }
    case 'first':
      return { value: nonEmpty[0] ?? null }
    case 'last':
      return { value: nonEmpty[nonEmpty.length - 1] ?? null }
    case 'min':
    case 'max':
    case 'avg': {
      const nums = nonEmpty.map(toNumber).filter((n): n is number => n !== null)
      if (nums.length === 0) return { value: null, display: '—' }
      let r: number
      if (fn === 'min') r = Math.min(...nums)
      else if (fn === 'max') r = Math.max(...nums)
      else r = nums.reduce((a, b) => a + b, 0) / nums.length
      const rounded = Math.round(r * 100) / 100
      return { value: rounded, display: String(rounded) }
    }
  }
}

// ─── 4. repeatable_array cell collapse ──────────────────────────────────────────

export function collapseArrayCell(
  arr: unknown,
  cat: CatalogField,
  strat: ArrayStrategy | undefined,
  formatEntry: (sub: CatalogField | undefined, v: unknown) => string,
): string {
  if (!Array.isArray(arr) || arr.length === 0) return '—'
  const mode = strat?.mode ?? 'join'
  const subByKey: Record<string, CatalogField> = {}
  for (const sf of cat.subFields ?? []) subByKey[sf.fieldKey] = sf

  if (mode === 'aggregate') {
    if (strat?.aggregate === 'sum' && strat.subFieldKey) {
      const sum = (arr as Record<string, unknown>[])
        .map((e) => toNumber(e?.[strat.subFieldKey!]))
        .filter((n): n is number => n !== null)
        .reduce((a, b) => a + b, 0)
      return String(Math.round(sum * 100) / 100)
    }
    return `${arr.length} مورد`
  }
  if (mode === 'separate') {
    // separate sub-grid is opened from the cell; the cell itself shows the count.
    return `${arr.length} مورد`
  }

  // join / wide → render each entry's chosen (or first) sub-field
  const subKey = strat?.subFieldKey ?? cat.subFields?.[0]?.fieldKey
  const sub = subKey ? subByKey[subKey] : undefined
  const sep = strat?.separator ?? '، '
  const entries = (arr as Record<string, unknown>[]).map((e) =>
    subKey ? formatEntry(sub, e?.[subKey]) : formatEntry(undefined, e),
  )
  const capped = mode === 'wide' && strat?.cap ? entries.slice(0, strat.cap) : entries
  return capped.join(sep)
}

// ─── 5. spec + RPC result → flat table ──────────────────────────────────────────

export interface ResolveTableOptions {
  /** scalar field formatter (type-aware, e.g. Jalali). */
  formatField: (cat: CatalogField | undefined, v: unknown) => string
}

/**
 * Fill one field column for a record-collapsed row (grouped single-form OR a form
 * inside a record-centric view), applying the wide/aggregate/pinned strategy and
 * repeatable_array collapse. `strat.mode` here is never 'long'.
 */
function fillCollapsedField(
  out: ResolvedRow,
  col: ViewColumn,
  cat: CatalogField | undefined,
  strat: FormInstanceStrategy,
  mergedByInstance: Record<string, unknown>[],
  instances: InstanceObj[],
  opts: ResolveTableOptions,
) {
  if (cat?.isRepeatableArray) {
    const raw = mergedByInstance[0]?.[col.key]
    if (col.array?.mode === 'separate' && Array.isArray(raw)) out.arrays[col.id] = raw as unknown[]
    out.display[col.id] = collapseArrayCell(raw, cat, col.array, opts.formatField)
    return
  }
  if (strat.mode === 'wide') {
    const cap = strat.cap ?? WIDE_DEFAULT_CAP
    for (let i = 0; i < cap; i++) {
      out.cells[`${col.id}@${i + 1}`] = mergedByInstance[i]?.[col.key] ?? null
    }
  } else if (strat.mode === 'pinned') {
    const idx = strat.pinnedIndex ?? 1
    const at = instances.findIndex((x) => x.instance_index === idx)
    out.cells[col.id] = at >= 0 ? mergedByInstance[at]?.[col.key] ?? null : null
  } else {
    const fn = strat.aggregate ?? 'last'
    const agg = aggregateField(fn, mergedByInstance.map((m) => m[col.key]))
    if (agg.display !== undefined) out.display[col.id] = agg.display
    else out.cells[col.id] = agg.value
  }
}

export function resolveTable(
  spec: DataViewSpec,
  catalog: FormCatalog,
  result: QueryFormViewResult,
  opts: ResolveTableOptions,
): ResolvedTable {
  const columns = resolveColumns(spec, catalog)
  const mode = spec.instanceStrategy.mode
  const fieldCols = spec.columns.filter((c) => c.source === 'field' && !c.hidden)
  const metaCols = spec.columns.filter((c) => c.source === 'record_meta' && !c.hidden)
  const rows: ResolvedRow[] = []

  const fillFieldCell = (
    out: ResolvedRow,
    colId: string,
    col: ViewColumn,
    merged: Record<string, unknown>,
  ) => {
    const cat = catalog.byKey[col.key]
    const raw = merged[col.key]
    if (cat?.isRepeatableArray) {
      if (col.array?.mode === 'separate' && Array.isArray(raw)) out.arrays[colId] = raw as unknown[]
      out.display[colId] = collapseArrayCell(raw, cat, col.array, opts.formatField)
    } else {
      out.cells[colId] = raw
    }
  }

  if (result.layout === 'long') {
    for (const r of result.rows as LongRpcRow[]) {
      const merged = withCalc(catalog, r.values)
      const out: ResolvedRow = {
        id: r.submission_id,
        recordId: r.record_id,
        displayId: r.display_id,
        recordStatus: r.record_status,
        submissionId: r.submission_id,
        instanceIndex: r.instance_index,
        softWarningCount: r.soft_warning_count ?? 0,
        formVersionId: r.form_version_id,
        cells: {},
        display: {},
        arrays: {},
      }
      for (const m of metaCols) {
        out.cells[m.id] =
          m.key === 'instance_index' ? r.instance_index
          : m.key === 'submission_status' ? r.submission_status
          : m.key === 'soft_warning_count' ? r.soft_warning_count
          : metaValue(r, m.key)
      }
      for (const col of fieldCols) fillFieldCell(out, col.id, col, merged)
      rows.push(out)
    }
    return { columns, rows, total: result.total }
  }

  // grouped layout → one row per record
  for (const r of result.rows as GroupedRpcRow[]) {
    const instances = (r.instances ?? []).slice().sort((a, b) => a.instance_index - b.instance_index)
    const out: ResolvedRow = {
      id: r.record_id,
      recordId: r.record_id,
      displayId: r.display_id,
      recordStatus: r.record_status,
      softWarningCount: instances.reduce((s, i) => s + (i.soft_warning_count ?? 0), 0),
      cells: {},
      display: {},
      arrays: {},
    }
    for (const m of metaCols) {
      out.cells[m.id] =
        m.key === 'submission_status' ? (instances[0]?.submission_status ?? null)
        : m.key === 'soft_warning_count' ? out.softWarningCount
        : metaValue(r, m.key)
    }

    const mergedByInstance = instances.map((i) => withCalc(catalog, i.values))

    for (const col of fieldCols) {
      fillCollapsedField(out, col, catalog.byKey[col.key], spec.instanceStrategy, mergedByInstance, instances, opts)
    }
    rows.push(out)
  }
  return { columns, rows, total: result.total }
}

// ─── 6. Record-centric (multi-form) base ────────────────────────────────────────

export interface RecordRpcRow {
  record_id: string
  display_id: string
  record_status: string
  record_number: number
  record_created_at: string
  record_updated_at: string
  owner_id: string | null
  /** form_id → its submissions for this record. */
  forms: Record<string, InstanceObj[]>
}

export interface RecordViewResult {
  total: number
  rows: RecordRpcRow[]
}

export interface RecordQueryArgs {
  forms: { form_id: string; field_keys: string[] }[]
  filters: { form_id: string; key: string; op: FilterOp; value?: string }[]
  sortKey: string | null
  sortDir: 'asc' | 'desc'
  includeDeleted: boolean
  search: string | null
}

export function resolveRecordQueryArgs(spec: DataViewSpec): RecordQueryArgs {
  const byForm: Record<string, Set<string>> = {}
  for (const c of spec.columns) {
    if (c.source !== 'field' || !c.formId) continue
    ;(byForm[c.formId] ??= new Set()).add(c.key)
  }
  return {
    forms: Object.entries(byForm).map(([form_id, keys]) => ({ form_id, field_keys: [...keys] })),
    filters: spec.filters
      .filter((f) => !!f.formId)
      .map((f) => ({ form_id: f.formId!, key: f.key, op: f.op, value: f.value })),
    sortKey: spec.sort?.key ?? null,
    sortDir: spec.sort?.dir ?? 'asc',
    includeDeleted: spec.includeDeleted ?? false,
    search: spec.search?.trim() || null,
  }
}

const strategyFor = (spec: DataViewSpec, formId: string): FormInstanceStrategy =>
  spec.formStrategies?.[formId] ?? DEFAULT_RECORD_FORM_STRATEGY

export function resolveRecordColumns(
  spec: DataViewSpec,
  catalogs: Record<string, FormCatalog>,
  formTitles: Record<string, string>,
): ResolvedColumn[] {
  const out: ResolvedColumn[] = []
  for (const col of spec.columns) {
    if (col.hidden) continue
    if (col.source === 'record_meta') {
      if (col.key === 'instance_index') continue // not meaningful per-record
      out.push({ id: col.id, label: col.label ?? metaLabel(col.key), kind: 'meta', metaKey: col.key, fieldType: metaType(col.key) })
      continue
    }
    const formId = col.formId
    if (!formId) continue
    const cat = catalogs[formId]?.byKey[col.key]
    const isCalc = !!catalogs[formId]?.calculatedByKey[col.key]
    const strat = strategyFor(spec, formId)
    const prefix = spec.formPrefix ? `${formTitles[formId] ?? ''}: ` : ''
    const produced = fieldColumnsFor(col, cat, isCalc, strat.mode, strat.cap ?? WIDE_DEFAULT_CAP)
    for (const p of produced) out.push({ ...p, label: `${prefix}${p.label}` })
  }
  return out
}

export function resolveRecordTable(
  spec: DataViewSpec,
  catalogs: Record<string, FormCatalog>,
  formTitles: Record<string, string>,
  result: RecordViewResult,
  opts: ResolveTableOptions,
): ResolvedTable {
  const columns = resolveRecordColumns(spec, catalogs, formTitles)
  const metaCols = spec.columns.filter((c) => c.source === 'record_meta' && !c.hidden && c.key !== 'instance_index')
  const fieldCols = spec.columns.filter((c) => c.source === 'field' && !c.hidden && c.formId)
  const rows: ResolvedRow[] = []

  for (const r of result.rows) {
    const allInstances = Object.values(r.forms ?? {}).flat()
    const out: ResolvedRow = {
      id: r.record_id,
      recordId: r.record_id,
      displayId: r.display_id,
      recordStatus: r.record_status,
      softWarningCount: allInstances.reduce((s, i) => s + (i.soft_warning_count ?? 0), 0),
      cells: {},
      display: {},
      arrays: {},
    }
    for (const m of metaCols) {
      out.cells[m.id] = m.key === 'soft_warning_count' ? out.softWarningCount
        : m.key === 'submission_status' ? null
        : metaValue(r, m.key)
    }
    for (const col of fieldCols) {
      const formId = col.formId!
      const cat = catalogs[formId]
      const instances = (r.forms?.[formId] ?? []).slice().sort((a, b) => a.instance_index - b.instance_index)
      const merged = cat ? instances.map((i) => withCalc(cat, i.values)) : instances.map((i) => i.values ?? {})
      fillCollapsedField(out, col, cat?.byKey[col.key], strategyFor(spec, formId), merged, instances, opts)
    }
    rows.push(out)
  }
  return { columns, rows, total: result.total }
}
